import React, { useEffect } from 'react'
import { ToastContainer, toast,Flip } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Module-level tracking to prevent duplicate toasts across component instances
const toastTracker = new Map();
const TOAST_DEBOUNCE_TIME = 1000; // 1 second debounce

export const SuccessToastMessage = ({ status, renderContainer = false, containerId }) => {
	useEffect(() => {
		// Create a unique key based on status message
		const toastKey = `success-${status}`;
		const now = Date.now();
		const lastShown = toastTracker.get(toastKey);

		// Only show toast if it hasn't been shown recently (within debounce time)
		if (!lastShown || (now - lastShown) > TOAST_DEBOUNCE_TIME) {
			const opts = {
				position: "top-right",
				autoClose: 3000, // Auto-close after 3 seconds
				toastId: toastKey, // Use consistent toastId to prevent duplicates
			};
			if (containerId != null && containerId !== '') {
				opts.containerId = containerId;
			}
			toast.success(status, opts);
			toastTracker.set(toastKey, now);
		}

		// Cleanup: remove old entries after debounce time
		const cleanup = setTimeout(() => {
			toastTracker.delete(toastKey);
		}, TOAST_DEBOUNCE_TIME);

		return () => {
			clearTimeout(cleanup);
		};
	}, [status, containerId]);

	return (
		<>
			{renderContainer && (
				<ToastContainer
					position="top-right"
					autoClose={3000}
					hideProgressBar={true}
					newestOnTop={true}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
					limit={3}
					stacked={true}
					style={{
						zIndex: 9999,
					}}
					toastStyle={{
						background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
						border: '1px solid #e5e5e5',
						borderRadius: '12px',
						boxShadow: 'transparent',
						padding: '8px 20px',
						minHeight: '64px',
						fontSize: '16px',
						lineHeight: '1.2',
						transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						right: '10px'
					}}
					transition={Flip}
					closeButton={false}
					className="custom-toast-container"
				/>
			)}
		</>

	)
}

export const ErrorToastMessage = ({ status = 'Something went wrong Please try again...', renderContainer = false }) => {

	useEffect(() => {
		// Create a unique key based on status message
		const toastKey = `error-msg-${status}`;
		const now = Date.now();
		const lastShown = toastTracker.get(toastKey);

		// Only show toast if it hasn't been shown recently (within debounce time)
		if (!lastShown || (now - lastShown) > TOAST_DEBOUNCE_TIME) {
			toast.error(status, {
				position: "top-right",
				autoClose: 3000,
				toastId: toastKey, // Use consistent toastId to prevent duplicates
			});
			toastTracker.set(toastKey, now);
		}

		// Cleanup: remove old entries after debounce time
		const cleanup = setTimeout(() => {
			toastTracker.delete(toastKey);
		}, TOAST_DEBOUNCE_TIME);

		return () => {
			clearTimeout(cleanup);
		};
	}, [status]);

	return (
		<>
			{renderContainer && (
				<ToastContainer
					position="top-right"
					autoClose={3000}
					hideProgressBar={true}
					newestOnTop={false}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
				/>
			)}
		</>
	)
}

export const ErrorToast = ({ status, renderContainer = false, containerId }) => {
	useEffect(() => {
		// Create a unique key based on status message
		const toastKey = `error-${status}`;
		const now = Date.now();
		const lastShown = toastTracker.get(toastKey);

		// Only show toast if it hasn't been shown recently (within debounce time)
		if (!lastShown || (now - lastShown) > TOAST_DEBOUNCE_TIME) {
			const opts = {
				position: "top-right",
				autoClose: 3000,
				toastId: toastKey, // Use consistent toastId to prevent duplicates
			};
			if (containerId != null && containerId !== '') {
				opts.containerId = containerId;
			}
			toast.error(status, opts);
			toastTracker.set(toastKey, now);
		}

		// Cleanup: remove old entries after debounce time
		const cleanup = setTimeout(() => {
			toastTracker.delete(toastKey);
		}, TOAST_DEBOUNCE_TIME);

		return () => {
			clearTimeout(cleanup);
		};
	}, [status, containerId]);

	return (
		<>
			{renderContainer && (
				<ToastContainer
					position="top-right"
					autoClose={3000}
					hideProgressBar={true}
					newestOnTop={true}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
					limit={3}
					stacked={true}
					style={{
						zIndex: 9999
					}}
					toastStyle={{
						background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
						border: '1px solid #e5e5e5',
						borderRadius: '12px',
						boxShadow: 'transparent',
						padding: '8px 20px',
						minHeight: '64px',
						fontSize: '16px',
						lineHeight: '1.2',
						transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						right: '10px'
					}}
					transition={Flip}
					closeButton={false}
					className="custom-toast-container"
				/>
			)}
		</>
	)
}

export const MissingPatientAlertMessage = () => {
	useEffect(() => {
		// Trigger the toast on component mount
		toast.info(
			<div>
				<h4>15 patients are missing from the records.</h4>
				<p>Check the alert button.</p>
			</div>,
			{
				position: "bottom-left", // Fixed at the bottom left
				autoClose: false, // Prevent auto-dismiss
				closeOnClick: true, // Allow closing on click
				hideProgressBar: true, // Hide progress bar
				draggable: false, // Prevent dragging the toast
				style: {
					background: "#fff",
					color: "#333",
					boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
					borderRadius: "8px",
					padding: "16px",
					maxWidth: "320px",
					fontFamily: "Arial, sans-serif",
				},
			}
		);
	}, []); // Empty dependency array ensures it runs only once on mount

	return (
		<div>
			{/* Toast Container */}
			{/* <ToastContainer /> */}
		</div>
	);
};

